import type {
  CampaignFilters,
  CampaignObjectiveKey,
  CampaignSpecialDateKey,
  CampaignStatusKey,
  LeadStageKey,
  TemplateApprovalKey,
} from '@crm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { PaginationMeta } from '@/modules/auth/types';

export const CAMPAIGNS_ROUTES = {
  LIST: '/marketing/campanhas',
  DETAIL: '/marketing/campanhas/:id',
  IMPORT: '/marketing/importar-contatos',
} as const;

export function campaignPath(id: string): string {
  return CAMPAIGNS_ROUTES.DETAIL.replace(':id', id);
}

const ENDPOINT = '/api/v1/campaigns';

export interface Campaign {
  id: string;
  /** Escrito pelo objetivo, no servidor - a tela nunca manda nome. */
  name: string;
  status: CampaignStatusKey;
  objective: CampaignObjectiveKey;
  specialDate: CampaignSpecialDateKey | null;
  scheduledAt: string | null;
  /** Em uma frase: quando esta campanha sai. */
  dispatchLabel: string;
  /** A hora em que a mensagem pode sair - `HH:mm`. */
  sendWindowStart: string | null;
  sendWindowEnd: string | null;
  body: string;
  templateId: string | null;
  /** O modelo escolhido e em que pe esta a aprovacao na Meta. */
  template: {
    id: string;
    name: string;
    body: string;
    contentSid: string | null;
    approval: TemplateApprovalKey;
    approvalNote: string | null;
    submittedAt: string | null;
    reviewedAt: string | null;
  } | null;
  couponId: string | null;
  couponCode: string | null;
  filters: CampaignFilters;
  lastRunAt: string | null;
  sentTotal: number;
  scheduledTotal: number;
  /** Leads que nasceram desta campanha - com algum, ela nao pode ser apagada. */
  leadsTotal: number;
  /** Leads que passaram do primeiro contato. */
  contactedTotal: number;
  /** Leads que chegaram a ser atendidos. */
  attendedTotal: number;
  createdAt: string;
}

export interface CampaignInput {
  status?: CampaignStatusKey;
  objective?: CampaignObjectiveKey;
  specialDate?: CampaignSpecialDateKey | null;
  scheduledAt?: string | null;
  body?: string;
  templateId?: string | null;
  couponId?: string | null;
  sendWindowStart?: string | null;
  sendWindowEnd?: string | null;
  filters?: CampaignFilters;
}

export interface CampaignRun {
  id: string;
  triggeredByName: string | null;
  total: number;
  sent: number;
  failed: number;
  startedAt: string;
  finishedAt: string | null;
}

export interface CampaignMetrics {
  sent: number;
  delivered: number;
  failed: number;
  funnel: { stage: LeadStageKey; total: number }[];
}

export function useCampaigns() {
  return useQuery({
    queryKey: ['campaigns'],
    queryFn: () => apiClient.get<{ data: Campaign[] }>(ENDPOINT),
  });
}

export function useCampaign(id: string) {
  return useQuery({
    queryKey: ['campaign', id],
    queryFn: () => apiClient.get<Campaign>(`${ENDPOINT}/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CampaignInput) => apiClient.post<Campaign>(ENDPOINT, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
  });
}

export function useUpdateCampaign(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CampaignInput) => apiClient.patch<Campaign>(`${ENDPOINT}/${id}`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign', id] });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });
}

export function useDeleteCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.delete<void>(`${ENDPOINT}/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
  });
}

/**
 * O tamanho do publico dos filtros que estao na tela - nao dos que estao
 * gravados. Sem isso o numero so mudava depois de salvar, e quem monta o
 * publico ficava filtrando as cegas.
 */
export interface CampaignPreview {
  total: number;
  sample: { id: string; name: string; phone: string }[];
  meta: PaginationMeta;
}

/** Manda a mensagem para o WhatsApp de quem esta montando a campanha. */
export function useTestCampaign(id: string) {
  return useMutation({
    mutationFn: () => apiClient.post<{ sent: boolean; to: string }>(`${ENDPOINT}/${id}/test`),
  });
}

export function useCampaignPreview(
  filters: CampaignFilters | undefined,
  objective: CampaignObjectiveKey = 'COUPON',
  page = 1,
  perPage = 20,
  search = '',
) {
  return useQuery({
    queryKey: ['campaign-preview', filters, objective, page, perPage, search],
    queryFn: () =>
      apiClient.post<CampaignPreview>(`${ENDPOINT}/recipients/preview`, {
        filters,
        objective,
        page,
        perPage,
        ...(search ? { search } : {}),
      }),
    enabled: Boolean(filters),
  });
}

export function useCampaignRuns(id: string) {
  return useQuery({
    queryKey: ['campaign-runs', id],
    queryFn: () => apiClient.get<{ data: CampaignRun[] }>(`${ENDPOINT}/${id}/runs`),
    enabled: Boolean(id),
    // O disparo acontece na fila: a tela precisa ver o resultado chegando.
    refetchInterval: 10_000,
  });
}

export function useCampaignMetrics(id: string) {
  return useQuery({
    queryKey: ['campaign-metrics', id],
    queryFn: () => apiClient.get<CampaignMetrics>(`${ENDPOINT}/${id}/metrics`),
    enabled: Boolean(id),
    refetchInterval: 10_000,
  });
}

export function useRunCampaign(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiClient.post<{ runId: string; total: number }>(`${ENDPOINT}/${id}/run`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-runs', id] });
      queryClient.invalidateQueries({ queryKey: ['campaign-metrics', id] });
    },
  });
}

export interface ImportRow {
  name?: string;
  phone?: string;
  whatsappName?: string;
  birthMonth?: string;
  birthYear?: string;
  gender?: string;
}

export interface ImportReport {
  id: string;
  fileName: string;
  total: number;
  created: number;
  updated: number;
  skipped: number;
  errors: { line: number; reason: string }[];
  createdAt: string;
}

export function useImportContacts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { fileName: string; unitId?: string | null; rows: ImportRow[] }) =>
      apiClient.post<ImportReport>('/api/v1/contacts/import', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customers'] }),
  });
}
